import moment from 'moment';
import { createSelector } from 'reselect';
import { AsyncJob, AsyncJobStatus, AsyncJobType } from 'types';
import { RootState } from '../reducer';

export function selectBackgroundTasks(state: RootState) {
  return state.backgroundTasks.tasks;
}

export const selectGroupedBackgroundTasks = createSelector(
  selectBackgroundTasks,
  tasks => {
    const delayedMessages = [
      AsyncJobType.RECALCULATE_COMPOSITE_TAG,
      AsyncJobType.ADD_INDEXED_DATA_CUTS,
      AsyncJobType.REMOVE_INDEXED_DATA_CUTS,
      AsyncJobType.RECALCULATE_DATA_MODEL_COMPOSITE_STATS,
      AsyncJobType.RECALCULATE_DATA_MODEL_WITH_AUTO_SPREAD,
    ];

    return Object.entries(
      tasks.filter((task) => {
        if (task.type === AsyncJobType.CLEANUP_CS_ADMIN_TENANCIES) {
          return false;
        }
        // filter messages that usually processed in couple seconds, to not spam users
        // almost always such messages will be removed before they become visible on UI
        if (delayedMessages.includes(task.type)) {
          return moment().diff(moment(task.createdAt), 'seconds') > 60;
        }
        return true;
      }).reduce(
        (result: Record<AsyncJobType, AsyncJob[]>, task) => {
          const taskGroup = result[task.type] || [];
          return {
            ...result,
            [task.type]: [...taskGroup, task],
          };
        },
        {} as Record<AsyncJobType, AsyncJob[]>,
      )
    ).reduce((result: { type: AsyncJobType, tasks: AsyncJob[], inProgress: boolean }[], [type, tasks]) => [
      ...result,
      {
        type: type as AsyncJobType,
        tasks,
        inProgress: !!tasks.find(task => task.status === AsyncJobStatus.IN_PROGRESS),
      },
    ], []);
  }
);

export const selectRecalculateCompositeTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.RECALCULATE_COMPOSITES || task.type === AsyncJobType.RECALCULATE_AGING_FACTOR
  )
)

export const selectPlanTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.MIGRATE_PLAN || task.type === AsyncJobType.UPDATE_PLAN
  )
)

export const selectExportTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.EXPORT_DATA
  )
)

export const selectRecalculateCompositesTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.RECALCULATE_COMPOSITES
  )
)

export const selectRecalculateDataModelCompositeStatsTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.RECALCULATE_DATA_MODEL_COMPOSITE_STATS
  )
)

export const selectRecalculateDataModelWithAutoSpreadTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.RECALCULATE_DATA_MODEL_WITH_AUTO_SPREAD
  )
)

export const selectPublishSurveyReplacementTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.PUBLISH_SURVEY_REPLACEMENT,
  )
)

export const selectApplyPolicySurveyReplacementTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.APPLY_CUT_SELECTION_POLICY_SURVEY_REPLACEMENT,
  )
)

export const selectProcessSrwCutSelectionResponseTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.PROCESS_SRW_CUT_SELECTION_RESPONSE,
  )
)

export const selectActualizeCompositeDataCutsSurveyReplacementTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.ACTUALIZE_COMPOSITE_DATA_CUT_REPLACEMENT,
  )
)

export const selectActualizeJobMappingsSurveyReplacementTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.ACTUALIZE_SURVEY_JOB_MAPPING_REPLACEMENT,
  )
)

export const selectHrisTestTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.TEST_HRIS_URL,
  )
)

export const selectPolicyManagerTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type ===  AsyncJobType.APPLY_WEIGHTING_POLICY || task.type === AsyncJobType.RESET_DEFAULT_CUTS
  )
)

// Used as the "complete" signal — only the final response job marks
// the end of the flow; the intermediate ADD_DEFAULT_DATA_CUTS_FOR_JOB_MAPPINGS
// is part of the same pipeline and would fire a premature reload.
export const selectAsyncJobMappingResponseTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.GET_SURVEY_DATA_BY_POLICY_RESPONSE,
  )
)

// GET_DEFAULT_DATA_CUTS_RESPONSE is the final job the BE schedules from
// applyPricingPolicy when the refined flow FF is on — used as the
// completion signal so consumers can reload after the cuts are persisted.
export const selectApplyPricingPolicyResponseTasks = createSelector(
  selectBackgroundTasks,
  (tasks) => tasks.filter((task) =>
    task.type === AsyncJobType.GET_DEFAULT_DATA_CUTS_RESPONSE,
  )
)
